/**
 * Modal for the "Attach knowledge source" flow on a knowledge gap.
 *
 * State machine:
 *
 *   upload:   picker -> validating -> confirm
 *   existing: picker -> direct resolve
 *
 * Existing-source rows display the verdict / confidence / rationale inline,
 * so clicking "Apply changes" resolves immediately — no separate review
 * page. Consent for any required org-wide state change (toggle flip / deck
 * activation) is implicit in the click; the backend treats the flags as
 * consent grants, not state assertions, and idempotently no-ops when the
 * change isn't needed.
 *
 * The modal is gated open by GapDetail when:
 *   summary.status === 'active'
 *   && summary.resolution_type === 'other'
 *   && category in ATTACHABLE_CATEGORIES
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Paperclip } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import knowledgeGapsService, {
  type KGAssetErrorCode,
  type KGAssetRef,
  type KGAssetType,
  type KGAssetValidationResponse,
  type KGRankedAsset,
  type KGSummaryResponse,
} from '@/lib/api/knowledgeGaps';
import { ExistingSourceTab } from './ExistingSourceTab';
import { UploadNewTab } from './UploadNewTab';
import { ValidationResultPanel } from './ValidationResultPanel';
import { ResolveConfirmationPanel } from './ResolveConfirmationPanel';

const ALL_TYPES: ReadonlyArray<KGAssetType> = ['video', 'presentation_deck'];
const VIDEO_ONLY: ReadonlyArray<KGAssetType> = ['video'];

function allowedAssetTypesForCategory(
  category: string,
): ReadonlyArray<KGAssetType> {
  return category === 'segment_mismatch' ? VIDEO_ONLY : ALL_TYPES;
}

type Phase = 'picker' | 'validating' | 'confirm';

type ResolveMutationInput = {
  assetRef?: KGAssetRef;
  enableToggle?: boolean;
  activateDeck?: boolean;
  source?: 'direct' | 'confirm';
};

export function AttachKnowledgeSourceModal({
  open,
  onOpenChange,
  orgId,
  summary,
  onResolved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  summary: KGSummaryResponse;
  onResolved: (updated: KGSummaryResponse) => void;
}) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>('picker');
  const [activeTab, setActiveTab] = useState<'existing' | 'upload'>('existing');
  const [selectedRef, setSelectedRef] = useState<KGAssetRef | null>(null);
  const [validation, setValidation] =
    useState<KGAssetValidationResponse | null>(null);
  const [enableToggle, setEnableToggle] = useState(true);
  const [activateDeck, setActivateDeck] = useState(true);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const allowedTypes = useMemo(
    () => allowedAssetTypesForCategory(summary.category),
    [summary.category],
  );

  // Reset state every time the modal opens or the summary changes so a
  // re-open after a successful resolve never re-uses stale state.
  useEffect(() => {
    if (!open) return;
    setPhase('picker');
    setActiveTab('existing');
    setSelectedRef(null);
    setValidation(null);
    setEnableToggle(true);
    setActivateDeck(true);
    setResolveError(null);
  }, [open, summary.id]);

  const validateMutation = useMutation({
    mutationFn: async (ref: KGAssetRef) => {
      const res = await knowledgeGapsService.validateAsset(orgId, summary.id, ref);
      if (!res.success || !res.data) {
        const code = (res.code as KGAssetErrorCode | undefined) || 'internal_error';
        throw new ValidateAssetError(
          res.error || 'Failed to validate asset',
          code,
          res.details,
        );
      }
      return res.data;
    },
    onSuccess: (data) => {
      setValidation(data);
      setEnableToggle(true);
      setActivateDeck(true);
      setPhase('confirm');
    },
    onError: (err: Error) => {
      const code = err instanceof ValidateAssetError ? err.code : 'internal_error';
      const description = describeAssetErrorForToast(err.message, code);
      toast({
        title: "Couldn't validate source",
        description,
        variant: 'destructive',
      });
      setSelectedRef(null);
      setPhase('picker');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (input?: ResolveMutationInput) => {
      const assetRef = input?.assetRef ?? selectedRef;
      if (!assetRef) {
        throw new Error('No asset selected');
      }
      const res = await knowledgeGapsService.resolveWithAsset(
        orgId,
        summary.id,
        {
          asset_ref: assetRef,
          enable_toggle: input?.enableToggle ?? enableToggle,
          activate_deck: input?.activateDeck ?? activateDeck,
        },
      );
      if (!res.success || !res.data) {
        const code = (res.code as KGAssetErrorCode | undefined) || 'internal_error';
        throw new ValidateAssetError(
          res.error || 'Failed to resolve gap',
          code,
          res.details,
        );
      }
      return res.data;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gaps-summaries', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gap-summary-detail', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gaps-report', orgId],
      });
      // Drop attach-source caches for this gap so a reopen on the same
      // (now resolved) gap doesn't show stale verdicts.
      queryClient.invalidateQueries({
        queryKey: ['kg-suggested-assets', orgId, summary.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['kg-validate-batch', orgId, summary.id],
      });
      toast({
        title: 'Gap resolved',
        description: updated.resolved_by_asset_title
          ? `Resolved with: ${updated.resolved_by_asset_title}`
          : 'Resolved with attached source.',
      });
      onResolved(updated);
      onOpenChange(false);
    },
    onError: (err: Error, variables) => {
      const code = err instanceof ValidateAssetError ? err.code : 'internal_error';
      const message = describeAssetErrorForToast(err.message, code);
      if (variables?.source === 'direct') {
        toast({
          title: "Couldn't resolve gap",
          description: message,
          variant: 'destructive',
        });
        return;
      }
      setResolveError(message);
    },
  });

  const handleSelectAsset = (ref: KGAssetRef) => {
    setSelectedRef(ref);
    setPhase('validating');
    setResolveError(null);
    validateMutation.mutate(ref);
  };

  const handleApplyExistingAsset = (asset: KGRankedAsset) => {
    const ref = asset.asset_ref;
    setSelectedRef(ref);
    setResolveError(null);
    // Verdict / confidence / rationale are shown inline in the existing-
    // source row, so we skip the validation page entirely. Both consent
    // flags go true: the backend treats them as consent grants (flip if
    // needed, otherwise no-op) rather than state assertions, so this is
    // safe even if our cached validation snapshot is stale relative to
    // current agent config. Deck activation is non-destructive — multiple
    // decks can be active concurrently per org, so we never deactivate a
    // sibling.
    resolveMutation.mutate({
      assetRef: ref,
      enableToggle: true,
      activateDeck: true,
      source: 'direct',
    });
  };

  const handleBackToPicker = () => {
    setSelectedRef(null);
    setValidation(null);
    setResolveError(null);
    setPhase('picker');
  };

  const isResolving = resolveMutation.isPending;
  const selectedAssetKey = selectedRef ? assetKeyFromRef(selectedRef) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/40 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attach a knowledge source
          </DialogTitle>
          <DialogDescription>
            Pick or upload a source that addresses this gap, then resolve.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Picker is always rendered. Hidden via `display: none` while
              the modal is in 'validating' or 'confirm' so the user's
              uploaded asset state (and the existing-source tab's React
              Query cache hits) survive a validate failure or a back
              navigation from the confirm view. Tearing this subtree down
              on every phase change is what caused upload context to
              vanish on validate-error. */}
          <div className={cn(phase !== 'picker' && 'hidden')}>
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as 'existing' | 'upload')
              }
            >
              <TabsList className="w-full">
                <TabsTrigger value="existing" className="flex-1">
                  Existing source
                </TabsTrigger>
                <TabsTrigger value="upload" className="flex-1">
                  Upload new
                </TabsTrigger>
              </TabsList>
              {/* forceMount keeps each tab's React subtree alive across
                  switches, so user state (scroll position, expanded
                  "Why this might help" rows, "Less likely" expander) is
                  preserved when the user toggles between tabs. The
                  data-[state=inactive]:hidden safety-belt prevents any
                  visual overlap regardless of Radix's internal hidden
                  attribute behavior. */}
              <TabsContent
                value="existing"
                forceMount
                className="mt-4 data-[state=inactive]:hidden"
              >
                <ExistingSourceTab
                  orgId={orgId}
                  summaryId={summary.id}
                  allowedTypes={allowedTypes}
                  onApply={handleApplyExistingAsset}
                  isApplying={resolveMutation.isPending || validateMutation.isPending}
                  selectedAssetKey={selectedAssetKey}
                />
              </TabsContent>
              <TabsContent
                value="upload"
                forceMount
                className="mt-4 data-[state=inactive]:hidden"
              >
                <UploadNewTab
                  orgId={orgId}
                  allowedTypes={allowedTypes}
                  onAssetReady={handleSelectAsset}
                  isReadyHandlerBusy={validateMutation.isPending}
                />
              </TabsContent>
            </Tabs>
          </div>

          {phase === 'validating' && (
            <div className="flex flex-col items-center justify-center gap-3 py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Reviewing this source against the gap…
              </p>
            </div>
          )}

          {phase === 'confirm' && validation && (
            <div className="space-y-6">
              <ValidationResultPanel result={validation} />
              <hr className="border-border/40" />
              <ResolveConfirmationPanel
                validation={validation}
                enableToggle={enableToggle}
                setEnableToggle={setEnableToggle}
                activateDeck={activateDeck}
                setActivateDeck={setActivateDeck}
                onConfirm={() => resolveMutation.mutate({ source: 'confirm' })}
                onBack={handleBackToPicker}
                isSubmitting={isResolving}
                errorMessage={resolveError}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

class ValidateAssetError extends Error {
  code: KGAssetErrorCode;
  details: unknown;
  constructor(message: string, code: KGAssetErrorCode, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

function describeAssetErrorForToast(
  defaultMessage: string,
  code: KGAssetErrorCode,
): string {
  switch (code) {
    case 'asset_not_ready':
      return 'This source is still being processed. Try again in a minute.';
    case 'invalid_asset_for_category':
      return 'This source type can\'t be attached to this gap.';
    case 'asset_would_be_ignored':
      return 'Resolving with this source would leave it invisible to your agent. Enable the required option and try again.';
    case 'agent_not_configured':
      return "This organization doesn't have a configured sales agent yet.";
    case 'gap_state_conflict':
      return 'This gap is no longer active or is already being resolved.';
    case 'gap_not_attachable':
      return "This gap doesn't accept attached knowledge sources.";
    case 'cross_org_forbidden':
      return 'That source belongs to a different organization.';
    case 'asset_not_found':
    case 'summary_not_found':
      return 'The source or gap could not be found.';
    default:
      return defaultMessage && defaultMessage.toLowerCase().includes('no asset selected')
        ? 'Select a source before continuing.'
        : 'We could not complete this action. Try again.';
  }
}

function assetKeyFromRef(ref: KGAssetRef): string {
  return `${ref.asset_type}:${ref.asset_id}`;
}
